/**
 * Avatar + AvatarGroup — user/entity image with fallback hierarchy.
 *
 * Renders:
 * 1. Remote image via `expo-image` with blurhash placeholder + fade-in
 * 2. Initials with deterministic background color (name hash)
 * 3. Default user icon (lucide `User`)
 *
 * Supports 4 sizes (sm → xl), online indicator dot, press interaction,
 * and grouped overlapping layout via AvatarGroup.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §5
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View, Text } from 'react-native'
import { Image } from 'expo-image'
import Animated from 'react-native-reanimated'
import { User } from '@/components/ui/icons'
import { Pressable } from './Pressable'
import { useAnimatedPress } from '../../lib/hooks/use-animated-press'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors } from '../../lib/theme/tokens'
import { cn } from '../../lib/theme/variants'
import { FONTS } from '../../lib/brand'
import { IMAGE_CACHE_POLICY, IMAGE_TRANSITION_MS } from '../../lib/utils/image-cache'
import { useEntrance } from '@/lib/hooks/use-entrance'

// ─── Size Tokens ────────────────────────────────────────────────────────────

const SIZE_MAP = {
  sm: { diameter: 32, fontSize: 12, dotSize: 8, iconSize: 16 },
  md: { diameter: 40, fontSize: 16, dotSize: 10, iconSize: 20 },
  lg: { diameter: 56, fontSize: 20, dotSize: 12, iconSize: 28 },
  xl: { diameter: 80, fontSize: 28, dotSize: 16, iconSize: 36 },
} as const

type AvatarSize = keyof typeof SIZE_MAP

// ─── Color Hash ─────────────────────────────────────────────────────────────

/**
 * Palette of background colors for initials fallback.
 * Deterministically selected by hashing the fallback string.
 */
const INITIALS_PALETTE = [
  '#F0422D', // coral
  '#480C82', // purple
  '#F9A826', // amber
  '#059669', // green
  '#2563EB', // blue
  '#7C3AED', // violet
  '#DB2777', // pink
  '#0891B2', // cyan
] as const

function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
    hash |= 0
  }
  return Math.abs(hash)
}

/**
 * The ONE place an initials avatar's colour is decided.
 *
 * The seed is normalised first (collapse whitespace, trim, lowercase) so the
 * same entity cannot land on two different palette entries just because one
 * surface passed `"CA"` and another passed `" ca "`. Callers that have a
 * stable id should still pass `colorSeed` — the initials themselves are a
 * lossy key (two different users share "CA") and are only the last resort.
 */
function colorForSeed(seed: string): string {
  const normalized = seed.replace(/\s+/g, ' ').trim().toLowerCase()
  return INITIALS_PALETTE[hashString(normalized) % INITIALS_PALETTE.length] as string
}

// ─── Avatar Props ───────────────────────────────────────────────────────────

interface AvatarProps {
  /** Remote image URI or local require() */
  source?: string | null
  /** Initials to display when image unavailable (max 2 chars) */
  fallback?: string
  /** Blurhash placeholder shown during image load */
  blurhash?: string
  /** Visual size */
  size?: AvatarSize
  /** Border shape */
  shape?: 'circle' | 'rounded'
  /** Show green online indicator dot */
  online?: boolean
  /**
   * Stable identifier (e.g. user.id) used to derive the initials-fallback
   * background color. Pass this for any user/entity that may be rendered
   * across multiple surfaces — guarantees the SAME entity always gets the
   * SAME palette color even if the fallback initials string differs
   * (e.g. "CA" in one place, "?" in another). Falls back to hashing the
   * initials when not provided.
   */
  colorSeed?: string | null
  /** Accessibility description of who this avatar represents */
  accessibilityLabel: string
  /** Optional press handler */
  onPress?: () => void
  /** Additional NativeWind className */
  className?: string
}

// ─── Avatar ─────────────────────────────────────────────────────────────────

export function Avatar({
  source,
  fallback,
  blurhash,
  size = 'md',
  shape = 'circle',
  online,
  colorSeed,
  accessibilityLabel,
  onPress,
  className,
}: AvatarProps) {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const tokens = SIZE_MAP[size]
  const [imageError, setImageError] = useState(false)

  const { animatedStyle, onPressIn, onPressOut } = useAnimatedPress({
    scaleTo: 0.93,
    disabled: !onPress,
  })

  const borderRadius = shape === 'circle' ? tokens.diameter / 2 : tokens.diameter * 0.2

  const initials = useMemo(() => {
    if (!fallback) return null
    // Every caller hands over a *name*, not a monogram — `displayName`, an
    // email, or '?'. Taking the first two characters therefore rendered "CA"
    // for "Camila Rivas": the wrong monogram, sitting directly above her own
    // name on the profile header. Take one letter per name part instead, and
    // only fall back to the first two characters for single-token fallbacks
    // (an email local part, "?").
    const parts = fallback.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean)
    const first = parts[0]
    if (!first) return null
    const last = parts.length > 1 ? parts[parts.length - 1] : undefined
    return (last ? `${first.charAt(0)}${last.charAt(0)}` : first.slice(0, 2)).toUpperCase()
  }, [fallback])

  const showImage = source && !imageError
  const showInitials = !showImage && initials
  const showIcon = !showImage && !showInitials

  useEffect(() => {
    setImageError(false)
  }, [source])

  const containerStyle = useMemo(
    () => ({
      width: tokens.diameter,
      height: tokens.diameter,
      borderRadius,
      overflow: 'hidden' as const,
    }),
    [tokens.diameter, borderRadius],
  )

  const content = (
    <View
      style={[
        containerStyle,
        {
          backgroundColor: showInitials
            ? colorForSeed(colorSeed || initials!)
            : showIcon
              ? isDark
                ? colors.surfaceRaised
                : colors.border
              : 'transparent',
          alignItems: 'center',
          justifyContent: 'center',
        },
      ]}
      accessibilityRole="image"
      accessibilityLabel={accessibilityLabel}
    >
      {showImage ? (
        <Animated.View
          collapsable={false}
          entering={enter()}
          style={{ width: '100%', height: '100%' }}
        >
          <Image
            source={source}
            placeholder={blurhash ? { blurhash } : undefined}
            style={{ width: '100%', height: '100%' }}
            contentFit="cover"
            transition={IMAGE_TRANSITION_MS}
            cachePolicy={IMAGE_CACHE_POLICY}
            onError={() => setImageError(true)}
          />
        </Animated.View>
      ) : null}

      {showInitials ? (
        <Text
          style={{
            fontSize: tokens.fontSize,
            fontFamily: FONTS.bodyBold,
            color: '#FFFFFF',
          }}
          // The one place in this app where capping the text multiplier is the
          // honest answer rather than the lazy one. `diameter` is a graphic
          // dimension, not a text box — it is the same disc whether it holds a
          // photo, an icon or two letters — so the monogram is decoration on a
          // fixed shape, and the name it stands for is already spoken by
          // `accessibilityLabel` on the container. Left uncapped, two letters
          // at 2.0 are wider than the 40dp disc and simply spill out of it.
          //
          // The cap is derived, not guessed: ~1.25em of advance width for two
          // Montserrat-Bold capitals, held inside 90% of the diameter.
          maxFontSizeMultiplier={(tokens.diameter * 0.9) / 1.25 / tokens.fontSize}
          numberOfLines={1}
          selectable={false}
        >
          {initials}
        </Text>
      ) : null}

      {showIcon && (
        <User
          size={tokens.iconSize}
          color={isDark ? colors.textSecondary : colors.textMuted}
          strokeWidth={1.8}
        />
      )}
    </View>
  )

  const wrappedContent = onPress ? (
    <Animated.View style={animatedStyle}>
      <Pressable
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        {content}
      </Pressable>
    </Animated.View>
  ) : (
    content
  )

  return (
    <View className={cn('relative', className)}>
      {wrappedContent}
      {online && (
        <View
          accessibilityLabel={t('a11y.user_online')}
          // Decoration, never a target. This dot is an absolutely-positioned
          // SIBLING of `wrappedContent` — which is a Pressable when `onPress`
          // is set — not a descendant of it. On web a click lands on the
          // topmost element and bubbles only through ancestors, so without
          // this the dot would be a dead corner on a tappable avatar. No call
          // site passes `online` today; the first one that does would ship it.
          pointerEvents="none"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: tokens.dotSize,
            height: tokens.dotSize,
            borderRadius: tokens.dotSize / 2,
            backgroundColor: colors.success,
            borderWidth: 2,
            borderColor: isDark ? colors.surface : '#FFFFFF',
          }}
        />
      )}
    </View>
  )
}
